import { HttpService } from 'core/modules/network-service';
import { get, merge } from 'lodash';

// local dependencies
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { REDUCER } from 'constants/entity-names';
import { getStore } from 'store';
import { getVisibleItems } from 'resolvers/getVisibleEntities';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberDetailAssistiveActionsNavWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberDetailAssistiveActionsNavWidgetService } from 'services/memberDetailAssistiveActionsNavWidget';
 *
 * // create service instance and send/cancel request
 * const memberDetailAssistiveActionsNavWidgetService = new MemberDetailAssistiveActionsNavWidgetService();
 * const result = await memberDetailAssistiveActionsNavWidgetService.fetchData( '<user-id>' ); // fetch data
 * memberDetailAssistiveActionsNavWidgetService.cancel(); // cancel an active request
 */
export class MemberDetailAssistiveActionsNavWidgetService extends HttpService {

    // make request and fetch data
    async fetchComponentData( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callerType
                    }
                } ),
            } );

            // extract component data
            const visibility = get( response, 'body.component.visibility', {} );
            const quickActions = get( response, 'body.component.structure.quickActions', [] );
            
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    quickActions,
                    visibility
                }
            };
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    async fetchSwitchDrugPreferernceData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId,
                }
            } ),
        } );

        // extract component data
        const fields = get( response, 'body.component.structure.fields', {} );
        const actions = get( response, 'body.component.structure.actions', [] );
        const values = get( response, 'body.component.data', [] );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                values,
                actions,
                fields
            }
        };
    }

    async setDrugPreference( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId,
                }
            } ),
        } );

        return {
            _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
        };
    }

    async fetchPriorAuthorizationData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId,
                }
            } ),
        } );

        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                data: get( response, 'body.component.data', [] ),
                ...get( response, 'body.component.structure', [] )
            }
        };
    }

    async fetchOutboundCallsData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId,
                }
            } ),
        } );

        // extract component data
        const values = get( response, 'body.component.data', [] );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                values,
                ...get( response, 'body.component.structure', [] )
            }
        };
    }

    async fetchWebsiteIssuesData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId,
                }
            } ),
        } );

        // extract component data
        const values = get( response, 'body.component.data', [] );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                values,
                ...get( response, 'body.component.structure', [] )
            }
        };
    }

    async fetchMemberEscalationCallData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId,
                }
            } ),
        } );

        // extract component data
        const values = get( response, 'body.component.data', [] );
        const fields = get( response, 'body.component.structure.fields', [] );
        const visibility = get( response, 'body.component.visibility' );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                values,
                ...get( response, 'body.component.structure', [] ),
                fields: getVisibleItems( fields, visibility )
            }
        };
    }

    async fetchMaterialMailOutData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId,
                }
            } ),
        } );

        // extract component data
        const values = get( response, 'body.component.data', [] );
        const fields = get( response, 'body.component.structure.fields', [] );
        const visibility = get( response, 'body.component.visibility' );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                values,
                ...get( response, 'body.component.structure', [] ),
                fields: getVisibleItems( fields, visibility )
            }
        };
    }

    async setPatientNotesData( { url, _data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( _data, {
                context: {
                    callId,
                }
            } ),
        } );

        // extract component data
        const values = get( response, 'body.component.data', [] );
        const fields = get( response, 'body.component.structure.fields', [] );
        const visibility = get( response, 'body.component.visibility' );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                values,
                ...get( response, 'body.component.structure', [] ),
                fields: getVisibleItems( fields, visibility ),
                visibility: visibility
            }
        };
    }

    // format error response
    _errorFormatter( errResponse ) {
        return errResponse; // some logic
    }

    // deactivate epost for member
    async deactivateInEpost( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                    }
                } ),
            } );

            // handle success
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    ...get( response, 'body.component.data.options', get( response, 'body.component.data', {} ) ),
                    ...get( response, 'body.component.structure', {} ),
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async sumbitDeActivateEpost( { url = {}, data = {} } = {} ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            // handle failure
            if( SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    resData: {
                        description: get( response, 'body.component.data.description', '' ),
                        values: get( response, 'body.component.data.values', {} ),
                        fields: get( response, 'body.component.structure.fields', [] ),
                    },
                };
            }

            return {
                _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.SUCCESS ),
            };
            
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                resData: null,
            };
        }
    }

    // submits Language preference
    async submitLanguagePreference( { url = {}, data = {} } = {} ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            // handle failure
            if( SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    resData: {
                        description: get( response, 'body.component.data.description', '' )
                    },
                };
            }

            return {
                _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.SUCCESS ),
            };
            
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                resData: null,
            };
        }
    }

    // impersonate member form
    async impersonateMember( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                    }
                } ),
            } );

            // extract data from the response
            const memberInfo = get( response, 'body.component.data.state.memberInfo', {} );
            const samlUrl = get( response, 'body.component.data.state.samlUrl', '' );
            const samlResponse = get( response, 'body.component.data.state.samlResponse', '' );
            const title = get( response, 'body.component.structure.title', '' );
            const description = get( response, 'body.component.structure.description', '' );
            const formConfig = get( response, 'body.component.structure.formConfig', {} );
            const actions = get( response, 'body.component.structure.actions', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.SUCCESS ),
                data: SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status' ) ? get( response, 'body.component.data', {} ) : {
                    memberInfo,
                    samlUrl,
                    samlResponse,
                    title,
                    description,
                    formConfig,
                    actions,
                }
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // create MSR epost for notes
    async epostMSRNotes( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                    }
                } ),
            } );

            const actions = get( response, 'body.component.structure.actions', [] );
            const title = get( response, 'body.component.structure.title', '' );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.SUCCESS ),
                data: { ...get( response, 'body.component', {} ), actions, title },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async addNotes( { url = {}, data = {} } = {} ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            if( SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {

                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    resData: {
                        description: get( response, 'body.component.data.description', {} ),
                    }
                };
            }

            return {
                _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.SUCCESS ),
            };
            
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async fetchBusinessProcessTodoInfo( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    options: get( response, 'body.component.data.options', {} ),
                    state: get( response, 'body.component.data.state', {} ),
                    ...get( response, 'body.component.structure', {} ),
                }
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async fetchMemberServicesEscalationsInfo( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    options: get( response, 'body.component.data.options', {} ),
                    state: get( response, 'body.component.data.state', {} ),
                    ...get( response, 'body.component.structure', {} ),
                }
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // fetch todo info when user clicks Status of Claim quick action
    async fetchStatusOfClaimTodoInfo( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId
                }
            } ),
        } );

        // extract component data
        const values = get( response, 'body.component.data', [] );
        const fields = get( response, 'body.component.structure.fields', [] );
        const visibility = get( response, 'body.component.visibility' );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                values,
                ...get( response, 'body.component.structure', [] ),
                fields: getVisibleItems( fields, visibility )
            }
        };
    }

    // fetch todo info when user clicks Add Insurance/Discount Card quick action
    async fetchAddInsuranceDiscountCardTodoInfo( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId
                }
            } ),
        } );

        // extract component data
        const values = get( response, 'body.component.data', [] );
        const fields = get( response, 'body.component.structure.fields', [] );
        const visibility = get( response, 'body.component.visibility' );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                values,
                ...get( response, 'body.component.structure', [] ),
                fields: getVisibleItems( fields, visibility )
            }
        };
    }

    // fetch widget info when user clicks CMS action
    async fetchCmsWidgetInfo( { url, data  } ) {

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data
        } );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                data: get( response, 'body.component.data', {} ),
                ...get( response, 'body.component.structure', {} )
            }
        };
    }

    // fetch widget info when user clicks Language preference
    async fetchLanguagePreferenceWidgetInfo( { url, data  } ) {

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data
        } );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                data: get( response, 'body.component.data', {} ),
                ...get( response, 'body.component.structure', {} )
            }
        };
    }

    // fetch widget info when user clicks Personal Id action
    async fetchPersonalIdWidgetInfo( { url, data  } ) {

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data
        } );
        
        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                data: get( response, 'body.component.data', {} ),
                ...get( response, 'body.component.structure', {} )
            }
        };
    }

    // fetch widget info when user clicks Service Now action
    async fetchServiceNowForm( { url, data  } ) {

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data
        } );

        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                ...response.body.component.structure,
                options: response.body.component.data.options,
                fields: getVisibleItems( response.body.component.structure.fields, response.body.component.visibility )
            }
        };
    }

    // fetch widget info when user clicks Ivr get pin
    async ivrPin( { url, data  } ) {

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data
        } );

        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                data: get( response, 'body.component.data', {} ),
                ...get( response, 'body.component.structure', {} )
            }
        };
    }
}
